import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import vazoulLogo from "@/assets/vazoul-logo.png";
import {
  Loader2,
  Search,
  DollarSign,
  Megaphone,
  Package,
  TrendingUp,
  Sparkles,
  ArrowRight,
  Zap,
  Shield,
  Globe,
  ChevronDown,
  Sun,
  Moon,
  Users,
  Truck,
  Brain,
  ShoppingCart,
  Boxes } from
"lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { LanguageSelector } from "@/components/LanguageSelector";

const featureIcons = [Search, Sparkles, Megaphone, Users, Truck, Package, DollarSign, Brain];
const featureGradients = [
"from-[hsl(262,80%,60%)] to-[hsl(280,75%,55%)]",
"from-[hsl(330,85%,60%)] to-[hsl(350,80%,55%)]",
"from-[hsl(145,70%,45%)] to-[hsl(160,65%,40%)]",
"from-[hsl(210,80%,55%)] to-[hsl(230,75%,50%)]",
"from-[hsl(45,90%,55%)] to-[hsl(30,85%,50%)]",
"from-[hsl(180,70%,50%)] to-[hsl(200,80%,55%)]",
"from-[hsl(280,75%,55%)] to-[hsl(300,70%,50%)]",
"from-[hsl(0,70%,55%)] to-[hsl(330,85%,60%)]"];

const stepIconComponents = [TrendingUp, ShoppingCart, Boxes, Brain];

const MARQUEE_BRANDS = [
"Shopify", "WooCommerce", "Salla", "Youcan", "Jumia",
"Amazon", "eBay", "Etsy", "BigCommerce", "Magento",
"PrestaShop", "OpenCart", "Wix", "Squarespace", "Lazada",
"Mercado Libre", "Flipkart", "Rakuten", "Zalando", "SHEIN",
"TikTok Shop", "Meta Ads", "Google Ads", "Alibaba", "AliExpress",
"Aramex", "DHL", "FedEx", "Stripe", "PayPal"];


export default function Auth() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const { theme, toggleTheme } = useTheme();

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      if (error) throw error;
      toast.success("Password reset email sent! Check your inbox.");
      setForgotPassword(false);
      setForgotEmail("");
    } catch (err: any) {
      toast.error(err.message || "Failed to send reset email");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        toast.success("Logged in successfully");
      } else {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { display_name: displayName },
            emailRedirectTo: window.location.origin
          }
        });
        if (error) throw error;
        if (data.session) {
          toast.success("Account created successfully!");
          await new Promise((r) => setTimeout(r, 1500));
          navigate("/");
          return;
        } else {
          toast.success("Check your email to confirm your account");
        }
      }
    } catch (err: any) {
      toast.error(err.message || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const scrollToFeatures = () => {
    document.getElementById("features")?.scrollIntoView({ behavior: "smooth" });
  };

  const features = Array.from({ length: 8 }, (_, i) => ({
    IconComp: featureIcons[i],
    title: t(`landing.feature${i + 1}Title`),
    description: t(`landing.feature${i + 1}Desc`),
    gradient: featureGradients[i]
  }));

  const steps = [
  { step: "01", title: t("landing.step1Title"), desc: t("landing.step1Desc"), IconComp: stepIconComponents[0] },
  { step: "02", title: t("landing.step2Title"), desc: t("landing.step2Desc"), IconComp: stepIconComponents[1] },
  { step: "03", title: t("landing.step3Title"), desc: t("landing.step3Desc"), IconComp: stepIconComponents[2] },
  { step: "04", title: t("landing.step4Title"), desc: t("landing.step4Desc"), IconComp: stepIconComponents[3] }];


  return (
    <main className="min-h-screen bg-background overflow-x-hidden">
      {/* ===== TOP BAR ===== */}
      <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 sm:px-6 py-2 sm:py-3 glass-strong">
        <div className="flex items-center gap-3">
          <img alt="Vazoul" className="h-8 w-8 rounded-xl" loading="eager" width={32} height={32} src="/lovable-uploads/vazoul-logo-64.png" />
          <span className="text-sm font-bold text-foreground">Vazoul AI</span>
        </div>
        <div className="flex items-center gap-2">
          <LanguageSelector />
          <button
            onClick={toggleTheme}
            className="flex items-center justify-center h-9 w-9 rounded-lg hover:bg-secondary/50 text-muted-foreground hover:text-foreground transition-colors"
            title={theme === "dark" ? t("theme.switchToLight") : t("theme.switchToDark")}>
            
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* ===== HERO SECTION ===== */}
      <div className="relative min-h-[100dvh] flex items-center pt-14 sm:pt-0">
        {/* CSS-only ambient background - no JS animations */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[10%] left-[5%] w-[400px] h-[400px] rounded-full bg-primary/[0.06] blur-[100px] animate-float-slow" />
          <div className="absolute bottom-[10%] right-[5%] w-[300px] h-[300px] rounded-full bg-[hsl(330,85%,60%,0.05)] blur-[100px] animate-float-slow [animation-delay:3s]" />
          <div className="absolute inset-0 bg-[linear-gradient(hsl(var(--border)/0.03)_1px,transparent_1px),linear-gradient(90deg,hsl(var(--border)/0.03)_1px,transparent_1px)] bg-[size:64px_64px]" />
        </div>

        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-16 lg:py-0">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-8 sm:gap-16 lg:gap-20 items-center">
            {/* Left: Platform Info */}
            <div className="space-y-5 sm:space-y-8 animate-fade-in-up">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full glass text-[11px] sm:text-xs font-semibold text-primary">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                </span>
                {t("landing.trialBadge")}
              </div>

              {/* Title */}
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center gap-4">
                  <img alt="Vazoul" className="h-10 w-10 sm:h-12 sm:w-12 rounded-2xl" loading="eager" fetchPriority="high" width={48} height={48} src="/lovable-uploads/vazoul-logo-96.png" />
                  <h1 className="text-3xl sm:text-4xl lg:text-6xl font-black tracking-tight leading-[1.1]">
                    <span className="gradient-text">Vazoul</span>{" "}
                    <span className="text-foreground">AI</span>
                  </h1>
                </div>
                <p className="text-base sm:text-lg lg:text-xl text-muted-foreground leading-relaxed max-w-lg">
                  {t("landing.heroSubtitle")}{" "}
                  <span className="text-foreground font-medium">{t("landing.heroPowered")}</span>
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                {[
                { value: "30+", label: t("landing.statPhases") },
                { value: "100%", label: t("landing.statDataDriven") },
                { value: "COD", label: t("landing.statOptimized") }].
                map((stat, i) =>
                <div key={i} className="glass rounded-xl sm:rounded-2xl p-3 sm:p-4 text-center">
                    <span className="text-2xl sm:text-3xl lg:text-4xl font-black gradient-text">{stat.value}</span>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] mt-1 font-semibold">{stat.label}</p>
                  </div>
                )}
              </div>

              {/* Trust points */}
              <div className="space-y-2 sm:space-y-3 hidden sm:block">
                {[
                { icon: Shield, text: t("landing.trustMargin") },
                { icon: Zap, text: t("landing.trustPipeline") },
                { icon: Brain, text: t("landing.trustAIPowered") },
                { icon: Globe, text: t("landing.trustMENA") }].
                map((point, i) => {
                  const Icon = point.icon;
                  return (
                    <div key={i} className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-muted-foreground">
                      <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
                        <Icon className="h-4 w-4" />
                      </div>
                      <span>{point.text}</span>
                    </div>);

                })}
              </div>
            </div>

            {/* Right: Auth Form */}
            <div className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto animate-fade-in-up [animation-delay:200ms]">
              <div className="relative">
                <div className="absolute -inset-1 rounded-3xl bg-gradient-to-b from-primary/20 via-transparent to-transparent blur-xl pointer-events-none" />
                <div className="relative glass-strong rounded-2xl sm:rounded-3xl p-5 sm:p-8 space-y-4 sm:space-y-6">
                  <div className="text-center space-y-2">
                    <h2 className="text-lg sm:text-xl font-bold text-foreground">
                      {isLogin ? t("landing.welcomeBack") : t("landing.startTrial")}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {isLogin ? t("landing.signInSubtitle") : t("landing.signUpSubtitle")}
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                    {!isLogin &&
                    <div className="space-y-2">
                        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t("landing.displayName")}</label>
                        <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder={t("landing.displayNamePlaceholder")} required={!isLogin}
                      className="w-full bg-background/60 border border-border rounded-xl px-4 py-2.5 sm:py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20 transition-all" />
                      </div>
                    }
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t("landing.email")}</label>
                      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required
                      className="w-full bg-background/60 border border-border rounded-xl px-4 py-2.5 sm:py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20 transition-all" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t("landing.password")}</label>
                      <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required minLength={6} autoComplete={isLogin ? "current-password" : "new-password"}
                      className="w-full bg-background/60 border border-border rounded-xl px-4 py-2.5 sm:py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20 transition-all" />
                    </div>
                    {isLogin &&
                    <div className="flex justify-end -mt-1">
                        <button type="button" onClick={() => setForgotPassword(true)} className="text-xs text-primary hover:underline">
                          {t("landing.forgotPassword")}
                        </button>
                      </div>
                    }

                    <button type="submit" disabled={loading}
                    className="w-full btn-gradient text-primary-foreground py-3 rounded-xl text-sm font-bold transition-all hover:shadow-lg hover:shadow-primary/25 disabled:opacity-50 flex items-center justify-center gap-2 active:scale-[0.98]">
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                      {isLogin ? t("landing.signIn") : t("landing.startFreeTrial")}
                    </button>

                    <div className="relative my-1">
                      <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border/50" /></div>
                      <div className="relative flex justify-center text-xs">
                        <span className="bg-card/80 px-3 text-muted-foreground/60">{t("landing.orContinueWith")}</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <button type="button" onClick={async () => {
                        const { error } = await lovable.auth.signInWithOAuth("google", { redirect_uri: window.location.origin });
                        if (error) toast.error(error.message || "Google sign-in failed");
                      }} className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border border-border bg-background/40 text-foreground hover:bg-muted/60 transition-all">
                        <svg className="h-4 w-4" viewBox="0 0 24 24">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                        </svg>
                        Google
                      </button>
                      <button type="button" onClick={async () => {
                        const { error } = await lovable.auth.signInWithOAuth("apple", { redirect_uri: window.location.origin });
                        if (error) toast.error(error.message || "Apple sign-in failed");
                      }} className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border border-border bg-background/40 text-foreground hover:bg-muted/60 transition-all">
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                        </svg>
                        Apple
                      </button>
                    </div>

                    <p className="text-center text-xs text-muted-foreground">
                      {isLogin ? t("landing.noAccount") : t("landing.haveAccount")}{" "}
                      <button type="button" onClick={() => setIsLogin(!isLogin)} className="text-primary hover:underline font-semibold">
                        {isLogin ? t("landing.signUp") : t("landing.signInLink")}
                      </button>
                    </p>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator - CSS only */}
        <button aria-label="Scroll to features" onClick={scrollToFeatures} className="absolute bottom-8 left-1/2 -translate-x-1/2 text-muted-foreground/40 hover:text-muted-foreground transition-colors animate-bounce">
          <ChevronDown className="h-6 w-6" />
        </button>
      </div>

      {/* ===== FEATURES SECTION ===== */}
      <section id="features" className="relative py-14 sm:py-24 lg:py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/[0.02] to-transparent pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center space-y-3 sm:space-y-4 mb-8 sm:mb-16">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs font-semibold text-primary">
              <Sparkles className="h-3 w-3" /> {t("landing.featuresTag")}
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-black tracking-tight">
              <span className="gradient-text">{t("landing.featuresTitle1")}</span><br />
              <span className="text-foreground">{t("landing.featuresTitle2")}</span>
            </h2>
            <p className="text-muted-foreground text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">{t("landing.featuresSubtitle")}</p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
            {features.map((feature, i) => {
              const Icon = feature.IconComp;
              return (
                <div key={i} className="group relative rounded-xl sm:rounded-2xl border border-border bg-card/40 p-3 sm:p-6 space-y-2 sm:space-y-4 hover:border-primary/20 transition-all duration-300 hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-1">
                  <div className={`h-9 w-9 sm:h-11 sm:w-11 rounded-lg sm:rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center text-primary-foreground shadow-lg`}>
                    <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>
                  <h3 className="text-xs sm:text-sm font-bold text-foreground">{feature.title}</h3>
                  <p className="text-[10px] sm:text-xs text-muted-foreground leading-relaxed">{feature.description}</p>
                  <div className="absolute bottom-0 left-3 right-3 sm:left-6 sm:right-6 h-[2px] bg-gradient-to-r from-transparent via-primary/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>);

            })}
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="relative py-14 sm:py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center space-y-3 sm:space-y-4 mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-black tracking-tight">
              <span className="text-foreground">{t("landing.howItWorksTitle1")}</span>
              <span className="gradient-text">Vazoul AI</span>
              <span className="text-foreground">{t("landing.howItWorksTitle2")}</span>
            </h2>
            <p className="text-muted-foreground text-sm sm:text-base max-w-xl mx-auto">{t("landing.howItWorksSubtitle")}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8 relative">
            <div className="hidden md:block absolute top-10 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-primary/20 via-primary/40 to-primary/20" />
            {steps.map((step, i) => {
              const Icon = step.IconComp;
              return (
                <div key={i} className="relative text-center space-y-2 sm:space-y-4">
                  <div className="relative inline-flex h-14 w-14 sm:h-20 sm:w-20 rounded-xl sm:rounded-2xl btn-gradient items-center justify-center text-primary-foreground mx-auto shadow-xl shadow-primary/20 hover:scale-110 transition-transform">
                    <Icon className="h-5 w-5" />
                    <span className="absolute -top-1.5 -right-1.5 sm:-top-2 sm:-right-2 h-5 w-5 sm:h-7 sm:w-7 rounded-full bg-background border-2 border-primary text-primary text-[8px] sm:text-[10px] font-black flex items-center justify-center">
                      {step.step}
                    </span>
                  </div>
                  <h3 className="text-sm sm:text-lg font-bold text-foreground">{step.title}</h3>
                  <p className="text-[10px] sm:text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
                </div>);

            })}
          </div>
        </div>
      </section>

      {/* ===== SOCIAL PROOF / TRUST ===== */}
      <section className="py-10 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="glass rounded-2xl sm:rounded-3xl p-5 sm:p-8 lg:p-12">
            <div className="grid sm:grid-cols-3 gap-5 sm:gap-8">
              {[
              { icon: Shield, title: t("landing.trustSecurity"), desc: t("landing.trustSecurityDesc") },
              { icon: Zap, title: t("landing.trustAI"), desc: t("landing.trustAIDesc") },
              { icon: Globe, title: t("landing.trustMultiCountry"), desc: t("landing.trustMultiCountryDesc") }].
              map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="flex gap-3 sm:gap-4">
                    <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg sm:rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                      <Icon className="h-5 w-5 sm:h-6 sm:w-6" />
                    </div>
                    <div>
                      <h3 className="text-xs sm:text-sm font-bold text-foreground mb-1">{item.title}</h3>
                      <p className="text-[10px] sm:text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                    </div>
                  </div>);

              })}
            </div>
          </div>
        </div>
      </section>

      {/* ===== FOOTER CTA ===== */}
      <section className="relative py-14 sm:py-24 lg:py-32">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[300px] rounded-full bg-primary/[0.04] blur-[100px]" />
        </div>
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center space-y-5 sm:space-y-6">
          <div className="space-y-3 sm:space-y-4">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-foreground">
              {t("landing.ctaTitle1")}{" "}
              <span className="gradient-text">{t("landing.ctaTitle2")}</span>{" "}
              {t("landing.ctaTitle3")}
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground max-w-lg mx-auto">{t("landing.ctaSubtitle")}</p>
          </div>
          <button onClick={() => {setIsLogin(false);window.scrollTo({ top: 0, behavior: "smooth" });}}
          className="inline-flex items-center gap-2 px-8 sm:px-10 py-3 sm:py-3.5 rounded-xl text-sm font-bold btn-gradient text-primary-foreground hover:shadow-xl hover:shadow-primary/25 transition-all active:scale-[0.98]">
            {t("landing.ctaButton")}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>

      {/* ===== TRUSTED BY MARQUEE ===== */}
      <section className="py-12 border-t border-border/30 overflow-hidden">
        <div className="text-center mb-8">
          <p className="text-sm text-muted-foreground">
            Trusted by <span className="text-primary font-bold">500+</span> businesses across the world and MENA region
          </p>
        </div>
        <div className="relative overflow-hidden">
          <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
          <div className="ticker-track">
            {[0, 1].map((setIdx) =>
            <div key={setIdx} className="ticker-group" aria-hidden={setIdx === 1}>
                {MARQUEE_BRANDS.map((brand, i) =>
              <span key={`${setIdx}-${i}`} className="text-xl font-bold text-muted-foreground/50 whitespace-nowrap select-none tracking-tight min-w-max">
                    {brand}
                  </span>
              )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ===== FORGOT PASSWORD MODAL ===== */}
      {forgotPassword &&
      <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-md flex items-center justify-center p-6">
          <div className="w-full max-w-md glass-strong rounded-3xl p-8 space-y-4 animate-fade-in-up">
            <div className="text-center space-y-2">
              <h2 className="text-lg font-bold text-foreground">{t("landing.resetTitle")}</h2>
              <p className="text-sm text-muted-foreground">{t("landing.resetSubtitle")}</p>
            </div>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t("landing.email")}</label>
                <input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="you@example.com" required
              className="w-full bg-background/60 border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/20 transition-all" />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => {setForgotPassword(false);setForgotEmail("");}}
              className="flex-1 py-2.5 rounded-xl text-sm font-medium border border-border text-muted-foreground hover:text-foreground transition-colors">
                  {t("common.cancel")}
                </button>
                <button type="submit" disabled={forgotLoading}
              className="flex-1 btn-gradient text-primary-foreground py-2.5 rounded-xl text-sm font-bold disabled:opacity-50 flex items-center justify-center gap-2">
                  {forgotLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                  {t("landing.sendResetLink")}
                </button>
              </div>
            </form>
          </div>
        </div>
      }
    </main>);

}